import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl, ValidatorFn, FormControl } from '@angular/forms';
import { FacilityService } from '../../facility/facility.service';
import { RequiredValidator } from '../../../customValidators/required/required';
import { CanComponentDeactivate } from '../../../can-deactivate.guard';
import { HelperService } from '../../../services/utils/helper.service';
import { Regex } from '../../../dataModels/regex';
import { CONSTANTS, DIALOG_MSGS, TABLE_CELL_WIDTH } from '../../../dataModels/constants';

/**
 * Contact Info component
 * Add/Update contact for facility, vendor, entity
 */
@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit, CanComponentDeactivate, OnChanges {
  /**
   * Form group for contact
   */
  contactFrm: UntypedFormGroup;

  /**
   * List of originally saved contact type
   */
  origContactTypeList = [];

  /**
   * List of contact Type
   */
  @Input() contactTypeList = [];

  /**
   * List of contacts that cannot be edited
   */
  @Input() nonEditList = [];

  /**
   * List of contacts that cannot be deleted
   */
  @Input() nonDeleteList = [];

  /**
   * mode for the contact ADD/UPDATE
   */
  @Input() mode;

  /**
   * mode for the contact ADD/UPDATE
   */
  @Input() updateEntityCheck = false;

  /**
   * Id for which contact has to be retrieved
   */
  @Input() id;

  /**
   * IdSeq for which contact has to be retrieved
   */
  @Input() idSeq;

  /**
   * boolean indicator to indicate component has to be refreshed
   */
  @Input() refresh;

  /**
   * broadcast contact list
   */
  @Output() contactInfoEvent = new EventEmitter();

  /**
   * Action for the contact, ADD/UPDATE
   */
  action = 'ADD';

  /**
   * Row data which is being edited
   */
  editRow = {} as any;

  /**
   * boolean indicator to indicate form is submitted, and to run validations
   */
  isAdd = false;

  /**
   * boolean indicator to indicate component is submitted
   */
  isSubmit = false;

  /**
   * Indicator to display busy loading
   */
  isBusy;

  /**
   * indicate any errors in the component
   */
  isError = false;

  /**
   * to display component section or error section
   */
  status;

  /**
   * Contact Info form data
   */
  contactInfoFormData = {
    hasData: false,
    data: [],
  };

  /**
   * List of headers for contact
   */
  contactHeaders = [
    {
      text: 'Contact Type',
      field: 'contactTypeDescr',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'Name',
      field: 'contactName',
      cssClass: TABLE_CELL_WIDTH.MEDIUM,
    },
    {
      text: 'Phone',
      field: 'phone',
      cssClass: TABLE_CELL_WIDTH.SMALLER,
    },
    {
      text: 'Email',
      field: 'email',
      cssClass: TABLE_CELL_WIDTH.LARGE,
    },
    {
      text: 'Fax',
      field: 'fax',
      cssClass: TABLE_CELL_WIDTH.SMALLER,
    },
  ];

  /**
   * List of contact data
   */
  @Input() contactDataList = [];

  /**
   * boolean to indicate if Add Contact is allowed
   */
  @Input() isAddContactAllowed = true;

  /**
   * boolean to indicate if Update Contact is allowed
   */
  @Input() isUpdateContactAllowed = true;

  /**
   * Constructor to inject dependency
   * @param helperService instance of helperService
   * @param facilityService instance of facilityService
   */
  constructor(private helperService: HelperService, private facilityService: FacilityService) { }

  /**
   * Initialize component
   */
  ngOnInit() {
    this.initFormControls();
    this.origContactTypeList = this.contactTypeList.map((x) => Object.assign({}, x));
    console.log("ngOnnint");
    this.getContact();
  }

  /**
   * Initialize form controls
   */
  initFormControls() {
    this.contactFrm = new UntypedFormGroup({
      contactType: new UntypedFormControl('', Validators.required),
      lastName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30),
      ]),
      firstName: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(2),
        Validators.maxLength(30),
      ]),
      middleName: new UntypedFormControl('', [
        RequiredValidator.validateRequired,
        Validators.pattern(Regex.ANOTHER_NAME),
        Validators.minLength(1),
        Validators.maxLength(30),
      ]),

      phone: new UntypedFormControl('', [Validators.required, this.customMinValidator(10)]),
      phoneExtn: new UntypedFormControl(''),
      fax: new UntypedFormControl(''),
      email: new UntypedFormControl('', [Validators.required, Validators.pattern(Regex.EMAIL)]),
    });
  }

  /**
   * getter for form controls
   */
  get f() {
    return this.contactFrm?.controls;
  }

  /**
   * Check if control is invalid
   * @param ctrl ctrl for which invalidity has to be checked
   */
  isControlInvalid(ctrl: AbstractControl) {
    return this.helperService.isControlInvalid(this.isAdd, ctrl);
  }

  /**
   * refresh the page
   */
  initUpdateContactForm() {
    if (this.mode === CONSTANTS.UPDATE && this.refresh) {
      console.log("form")
      this.getContact();
    }
  }

  /**
   * get originally added contacts
   */
  getContact() {
    console.log("getContact");
    console.log("id", this.id);
    console.log('ht', this.idSeq)
    if (this.id || this.idSeq) {
      this.isBusy = this.facilityService.getContact(this.id, this.idSeq).then((response) => {
        if (response.hasData) {
          this.contactDataList = response.contactList;
          this.getContactTypeList();
          this.canContactEditAndDelete();
        }
        this.isError = response.isError;
        if (!this.isError) {
          this.status = CONSTANTS.SUCCESS;
        }
        this.contactInfoEvent.emit(this.contactDataList);
      });
    }
  }

  customMinValidator(prefix: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined || control.value === '') {
        return null; // If the control value is empty, return null
      }

      // Remove non-numeric characters and get the length
      const numericLength = control.value.replace(/\D/g, '').length;
      let control1 = new FormControl();
      control1.setValue(control.value.replace(/\D/g, ''));
      // Check if the numeric length is less than the specified minimum length
      if (numericLength < prefix) {
        return Validators.minLength(prefix)(control1);
      }

      return null; // Validation passed
    };
  }

  /**
   * Add/Update contact
   */
  addContact() {
    this.isAdd = true;
    if (this.contactFrm.valid) {
      if (this.mode === CONSTANTS.ADD) {
        this.addContactToGrid();
        this.resetContact();
        this.contactInfoEvent.emit(this.contactDataList);
      } else if (this.mode === CONSTANTS.UPDATE) {
        this.manageContact(CONSTANTS.ADD);
      }
    }
  }

  /**
   * Add the contact to the grid
   */
  addContactToGrid() {
    const contactSeq = this.contactDataList.length + 1;
    this.contactDataList = this.contactDataList.concat(this.assignFormValuesToGrid(contactSeq));
    this.getContactTypeList();
  }

  /**
   * assign form values to the table
   */
  assignFormValuesToGrid(contactSeq) {
    const lastName = this.f.lastName.value.toUpperCase();
    const firstName = this.f.firstName.value.toUpperCase();
    const middleName = this.helperService.IsNullOrEmpty(this.f.middleName.value) ? '' : this.f.middleName.value?.toUpperCase();
    return {
      contactSeq,
      contactType: this.f.contactType.value,
      contactTypeDescr: this.getDescr(this.f.contactType.value),
      lastName,
      firstName,
      middleName,
      contactName: `${lastName}, ${firstName} ${middleName}`,
      phone: this.f.phone.value,
      phoneExtn: this.f.phoneExtn.value,
      email: this.f.email.value,
      fax: this.f.fax.value,
      canEdit: this.checkIfContactCanEdit(this.f.contactType.value),
      canDelete: this.checkIfContactCanDelete(this.f.contactType.value),
    };
  }

  /**
   * get contact type description
   */
  getDescr(value: string) {
    let contactDescr = '';

    const matchedContactList = this.origContactTypeList.filter((item) => {
      return item.columnValue === value;
    });

    if (matchedContactList.length >= 1) {
      contactDescr = matchedContactList[0].descr;
    }

    return contactDescr;
  }

  /**
   * determine if contact can be edited
   */
  checkIfContactCanEdit(contactType) {
    return this.nonEditList.indexOf(contactType) === -1;
  }

  /**
   * determine if contact can be deleted
   */
  checkIfContactCanDelete(contactType) {
    return this.nonDeleteList.indexOf(contactType) === -1;
  }

  /**
   * Edit Contact
   * @param event row edit event having row data
   */
  editContact(event) {
    if (this.isUpdateContactAllowed) {
      this.action = 'UPDATE';
      this.contactTypeList = this.origContactTypeList.map((x) => Object.assign({}, x));
      Object.assign(this.editRow, event.row);
      this.contactFrm.setValue({
        contactType: this.editRow.contactType,
        lastName: this.editRow.lastName,
        firstName: this.editRow.firstName,
        middleName: this.editRow.middleName,
        phone: this.editRow.phone,
        phoneExtn: this.editRow.phoneExtn,
        fax: this.editRow.fax,
        email: this.editRow.email,
      });
      this.contactFrm.controls.contactType.disable();
    }
  }

  /**
   * When user deletes contact
   * @param event args recieved from table component containing row
   */
  deleteContact(event) {
    this.helperService.showConfirmMessage(DIALOG_MSGS.CONFIRM_DELETE, ['Yes', 'No']).subscribe((value) => {
      if (value.toUpperCase() === CONSTANTS.YES && this.mode === CONSTANTS.UPDATE) {
        this.manageContact(CONSTANTS.DELETE, event.row);
      } else {
        this.contactDataList.splice(event.row.contactSeq - 1, 1);
        this.getContactTypeList();
      }
    });
  }

  // on update button click event
  /**
   * On Update contact, add data to grid when Mode is 'ADD' e.g. Create Facility, Create System
   * when Mode is 'UPDATE' make an API call to update contact
   */
  updateContact() {
    this.isAdd = true;
    if (this.contactFrm.valid) {
      if (this.mode === CONSTANTS.ADD) {
        const contactIndex = this.contactDataList.findIndex((contact) => contact.contactSeq === this.editRow.contactSeq);
        this.contactDataList[contactIndex] = this.assignFormValuesToGrid(this.contactDataList[contactIndex].contactSeq);
        this.resetContact();
      } else if (this.mode === CONSTANTS.UPDATE) {
        let isFormModified = false;
        for (const prop in this.contactFrm.value) {
          if (this.contactFrm.value.hasOwnProperty(prop)) {
            if (this.contactFrm.value[prop] !== this.editRow[prop]) {
              isFormModified = true;
              break;
            }
          }
        }
        if (isFormModified) {
          this.manageContact(this.mode);
        } else {
          this.helperService.showInfoMessage(CONSTANTS.NO_CHANGES_MADE_MSG);
        }
      }
    }
  }

  /**
   * call service to ADD, UPDATE or DELETE Contact
   * @param action action for manage contatc api, ADD, UPDATE or DELETE
   * @param row row for DELETE
   */
  manageContact(action, row?) {
    let origData;
    let contactData;

    if (action === CONSTANTS.DELETE) {
      origData = row;
      contactData = row;
    } else {
      origData = this.editRow;
      contactData = this.contactFrm.getRawValue();
    }
    this.isBusy = this.facilityService.manageContact(action, origData, contactData, this.id, this.idSeq, this.contactDataList).then((response) => {
      if (response.status === CONSTANTS.SUCCESS || response.status === CONSTANTS.TRN_WARNING) {
        if (action === CONSTANTS.ADD) {
          this.helperService.showSuccessMessage(DIALOG_MSGS.CONTACT_ADD_SUCCESS);
        } else if (action === CONSTANTS.UPDATE) {
          this.helperService.showSuccessMessage(DIALOG_MSGS.CONTACT_UPDATE_SUCCESS);
        } else if (action === CONSTANTS.DELETE) {
          this.helperService.showSuccessMessage(DIALOG_MSGS.CONTACT_DELETE_SUCCESS);
        }
        console.log("manageContact");
        this.getContact();
        this.resetContact();
      } else {
        this.helperService.showErrorMessage(response.message);
      }
    });
  }

  /**
   * Reset contact
   */
  resetContact() {
    this.editRow = {};
    this.isAdd = false;
    this.isSubmit = false;
    if (this.action === 'ADD') {
      this.contactFrm.reset();
    } else {
      this.getContactTypeList();
      this.contactFrm.reset();
    }
    this.action = 'ADD';
    this.contactFrm.markAsPristine();
    this.contactFrm.markAsUntouched();
    this.contactFrm.controls.contactType.enable();
  }

  resetContactWithData() {
    this.resetContact();
    if (this.action === 'ADD') {
      this.contactDataList = [];
      this.contactInfoEvent.emit(this.contactDataList);
      this.getContactTypeList();
    }
  }

  /**
   * reset contact list
   */
  reset() {
    this.resetContact();
    if (this.action === 'ADD') {
      this.contactDataList = [];
    }
  }

  /**
   * get contactType List, post adding contact remove the contact type from the list
   */
  getContactTypeList() {
    this.contactTypeList = [];
    let isContactTypeFound = false;
    for (const contactType of this.origContactTypeList) {
      for (const data of this.contactDataList) {
        isContactTypeFound = contactType.columnValue === data.contactType;
        if (isContactTypeFound) {
          break;
        }
      }

      if (!isContactTypeFound) {
        this.contactTypeList.push(contactType);
      }
    }
  }

  /**
   * Invoked from the wrapper component - manage facility info component
   * Returns the formData, also indicates if form is modified or if form has error
   */
  getFormData() {
    this.isSubmit = true;
    this.isAdd = true;
    if (this.contactDataList.length === 0) {
      this.contactInfoFormData.hasData = false;
      this.contactInfoFormData.data = [];
    } else {
      this.contactInfoFormData.hasData = true;
      this.contactInfoFormData.data = this.contactDataList;
    }

    return this.contactInfoFormData;
  }

  /**
   * decide if component can deactivate
   */
  canDeactivate() {
    return this.helperService.canDeactivate(this.contactFrm);
  }

  /**
   * on changes to the @Input() values refresh the component
   * @param changes change event
   */
  ngOnChanges(changes: SimpleChanges) {
    if (this.refresh) {
      this.origContactTypeList = this.contactTypeList.map((x) => Object.assign({}, x));
      this.initUpdateContactForm();
    } else if (!this.refresh) {
      this.status = '';
    }
  }

  /**
   * assign contact list with previously saved contact list, used for system and group
   */
  setContactData(data) {
    this.contactDataList = data;
    this.status = CONSTANTS.SUCCESS;
    this.canContactEditAndDelete();
    this.getContactTypeList();
  }

  /**
   * decide which rows in contact can edit and delete
   */
  canContactEditAndDelete() {
    for (const contact of this.contactDataList) {
      contact.canEdit = this.checkIfContactCanEdit(contact.contactType);
      contact.canDelete = this.checkIfContactCanDelete(contact.contactType);
    }
  }
}
